/**
 * Empfohlene Dauer einer Uebung: Spanne von-bis und Durchgaenge mit Pausen.
 *
 * Reine Rechen- und Formatierungsfunktionen ohne Schema (das steht in `training.ts`), damit
 * Server, Oberflaeche, Druck und das Bild einer Einheit dieselben Zahlen und Texte verwenden.
 *
 * Eine Uebung traegt `durationMinutes` ("von" bzw. Einzelwert), optional `durationMaxMinutes` ("bis")
 * und optional Durchgaenge: `sets` mal `setMinutes`, dazwischen `pauseMinutes`.
 * Gesamtdauer der Durchgaenge = sets x setMinutes + (sets - 1) x pauseMinutes.
 */
export const EXERCISE_LOAD_LIMITS = {
    /** Durchgaenge je Uebung. */
    sets: { min: 1, max: 20 },
    /** Minuten je Durchgang, in halben Schritten. */
    setMinutes: { min: 0.5, max: 60 },
    /** Pause zwischen zwei Durchgaengen in Minuten, in halben Schritten. */
    pauseMinutes: { min: 0, max: 30 },
    /** Feinster Schritt der Minuten je Durchgang und der Pause. */
    step: 0.5,
    /** Ab dieser Abweichung von der Gesamtdauer der Durchgaenge gilt ein geplanter Wert als deutlich anders (+/- 25 %). */
    tolerance: 0.25,
};
/** Ein Vielfaches von 0,5? (Fliesskommafehler werden verziehen.) */
export function isHalfStep(value) {
    return Number.isFinite(value) && Math.abs(value * 2 - Math.round(value * 2)) < 1e-9;
}
/** Gesamtdauer von `sets` Durchgaengen zu je `setMinutes` mit `pauseMinutes` zwischen je zwei Durchgaengen. */
export function setsTotalMinutes(sets, setMinutes, pauseMinutes = 0) {
    return sets * setMinutes + Math.max(0, sets - 1) * pauseMinutes;
}
/** Gesamtdauer der Durchgaenge der Uebung; `null`, wenn sie keine vollstaendigen Durchgaenge hat. */
export function loadTotalMinutes(load) {
    const { sets, setMinutes } = load;
    if (sets == null || setMinutes == null)
        return null;
    return setsTotalMinutes(sets, setMinutes, load.pauseMinutes ?? 0);
}
/**
 * Minuten, die beim Hinzufuegen zu einer Einheit gelten: die Gesamtdauer der Durchgaenge (auf ganze Minuten
 * gerundet, die Einheit rechnet in ganzen Minuten), sonst "bis", sonst "von". `null`, wenn nichts empfohlen ist.
 */
export function recommendedMinutes(load) {
    const total = loadTotalMinutes(load);
    if (total !== null)
        return Math.max(1, Math.round(total));
    return load.durationMaxMinutes ?? load.durationMinutes ?? null;
}
/** Zahl in Minuten fuer den Text: ganze Zahl ohne Nachkomma, halbe mit Komma (2,5). */
export function formatMinutes(value) {
    return Number.isInteger(value) ? String(value) : String(value).replace('.', ',');
}
/** "12–18 Min." bei einer Spanne, "15 Min." bei einem Wert; `null`, wenn keine Dauer gesetzt ist. */
export function formatDurationRange(load) {
    const from = load.durationMinutes ?? null;
    const to = load.durationMaxMinutes ?? null;
    if (from === null)
        return null;
    return to !== null && to > from ? `${formatMinutes(from)}–${formatMinutes(to)} Min.` : `${formatMinutes(from)} Min.`;
}
/** "4 × 3 Min., 1 Min. Pause" (ohne Pause: "4 × 3 Min."); `null` ohne vollstaendige Durchgaenge. Mit `withTotal` folgt "(15 Min.)". */
export function formatSets(load, withTotal = false) {
    const { sets, setMinutes } = load;
    if (sets == null || setMinutes == null)
        return null;
    const pause = load.pauseMinutes ?? 0;
    const parts = [`${sets} × ${formatMinutes(setMinutes)} Min.`];
    if (pause > 0 && sets > 1)
        parts[0] += `, ${formatMinutes(pause)} Min. Pause`;
    if (withTotal)
        parts.push(`(${formatMinutes(setsTotalMinutes(sets, setMinutes, pause))} Min.)`);
    return parts.join(' ');
}
/**
 * Die Dauer einer Uebung als kurze Texte: "10–15 Min." und/oder "4 × 3 Min., 1 Min. Pause (15 Min.)".
 * Wiederholt der Einzelwert nur die Gesamtdauer der Durchgaenge, steht er nicht zusaetzlich da.
 * Leer, wenn die Uebung keine Dauer traegt.
 */
export function formatLoadParts(load) {
    const range = formatDurationRange(load);
    const sets = formatSets(load, true);
    if (!sets)
        return range ? [range] : [];
    const total = loadTotalMinutes(load);
    const from = load.durationMinutes ?? null;
    const to = load.durationMaxMinutes ?? null;
    const repeatsTotal = from !== null && from === total && (to === null || to === from);
    return range && !repeatsTotal ? [range, sets] : [sets];
}
/**
 * Weicht der geplante Wert deutlich von der Empfehlung ab? Bei einer Spanne gilt "ausserhalb der Spanne", bei Durchgaengen
 * ohne Spanne "mehr als 25 % von der Gesamtdauer". Ein Einzelwert gilt als Richtwert und loest nichts aus.
 */
export function loadDeviation(minutes, load) {
    if (!Number.isFinite(minutes) || minutes <= 0)
        return null;
    const from = load.durationMinutes ?? null;
    const to = load.durationMaxMinutes ?? null;
    if (from !== null && to !== null && to > from) {
        if (minutes < from)
            return 'below';
        if (minutes > to)
            return 'above';
        return null;
    }
    const total = loadTotalMinutes(load);
    if (total === null)
        return null;
    const margin = total * EXERCISE_LOAD_LIMITS.tolerance;
    if (minutes < total - margin)
        return 'below';
    if (minutes > total + margin)
        return 'above';
    return null;
}
//# sourceMappingURL=training-load.js.map